import { useEffect, useState } from 'react';
import { useProfileContext } from './useProfileContext';
import { apiGetPdfLayoutEffective } from '../api';

/**
 * Loads the Rider Layout field order for one section (effective = global
 * template merged with the band override, like ConfigGeneralPage).
 *
 * Returns the ordered list of field ids for the given section id
 * (e.g. 'sound', 'iem', 'lights', 'stage-design', 'audio-backline',
 * 'hospitality'), or null when no layout / no field list is present — callers
 * fall back to their default JSX order in that case.
 *
 * Usage: `const layoutOrder = useLayoutOrder('sound');` then reorder your
 * page's blocks with `sortByLayoutOrder(blocks, layoutOrder, (b) => b.id)`.
 */
export function useLayoutOrder(sectionId: string): string[] | null {
  const { activeProfileId } = useProfileContext();
  const [layoutOrder, setLayoutOrder] = useState<string[] | null>(null);

  useEffect(() => {
    if (!activeProfileId) {
      setLayoutOrder(null);
      return;
    }
    let cancelled = false;
    apiGetPdfLayoutEffective(activeProfileId)
      .catch(() => null)
      .then((effective: any) => {
        if (cancelled) return;
        const section = (effective?.sections || []).find((s: any) => s.id === sectionId);
        if (section && Array.isArray(section.fields) && section.fields.length > 0) {
          setLayoutOrder(section.fields.map((f: any) => f.id));
        } else {
          setLayoutOrder(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeProfileId, sectionId]);

  return layoutOrder;
}
